import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import PlaquinhaConfig from '../components/PlaquinhaConfig';

// O PDF vem como blob — quando o backend devolve erro, a mensagem JSON vem dentro do blob
async function erroDoPdf(err) {
  try {
    const d = err.response?.data;
    if (d instanceof Blob) return JSON.parse(await d.text()).error || 'Erro ao gerar plaquinhas';
    return d?.error || 'Erro ao gerar plaquinhas';
  } catch { return 'Erro ao gerar plaquinhas'; }
}

// Abas "por loja": de onde vem a lista e o que já entra marcado em cada loja.
const SUGESTAO_POR_LOJA = {
  'por-loja': { rota: 'sugestao-por-loja', sugerido: (p, filial) => (p.isPiso ? p.amostra[filial] > 0 : true) },
  'por-estoque': { rota: 'sugestao-por-estoque', sugerido: (p, filial) => p.estoque[filial] > 0 },
};

export default function PlaquinhasPage() {
  const [searchParams] = useSearchParams();
  const campanhaIdParam = searchParams.get('campanha');

  const [aba, setAba] = useState(campanhaIdParam ? 'campanha' : 'avulsa');
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState(campanhaIdParam || '');
  const [produtosCampanha, setProdutosCampanha] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [gerando, setGerando] = useState(false);

  // Filtros e ordenação (aba campanha)
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroDisp, setFiltroDisp] = useState('');
  const [filtroClasse, setFiltroClasse] = useState('');
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });

  // Avulsa — busca
  const [buscaCodpro, setBuscaCodpro] = useState('');
  const [buscaDescricao, setBuscaDescricao] = useState('');
  const [resultados, setResultados] = useState([]);
  const [itensAvulsos, setItensAvulsos] = useState([]);

  // Manual
  const [manual, setManual] = useState({ codigo: '', nome: '', precoDE: '', precoPOR: '', comissao: '', tipo: 'linha' });

  // Por loja: mostruário ('por-loja') ou estoque bruto ('por-estoque'). Mesma tela, muda o critério.
  const [sugLojas, setSugLojas] = useState(null);       // { modo, lojas: [{filial}], produtos: [{id, codpro, descricao, isPiso, amostra} | {…, estoque}] }
  const [filiaisMap, setFiliaisMap] = useState({});     // '04' -> 'Central'
  const [selPorLoja, setSelPorLoja] = useState({});     // filial -> [ids]
  const [lojaExpandida, setLojaExpandida] = useState(null);
  const [gerandoLoja, setGerandoLoja] = useState(null); // filial em geração
  const [carregandoSug, setCarregandoSug] = useState(false);

  useEffect(() => {
    axios.get(`${API}/campanhas`).then(res => setCampanhas(res.data)).catch(() => {});
    axios.get(`${API}/filiais`).then(res => {
      const m = {}; res.data.forEach(f => { m[String(f.codigo).padStart(2, '0')] = f.nome; });
      setFiliaisMap(m);
    }).catch(() => {});
  }, []);

  // Carrega a sugestão por loja quando entra na aba / troca campanha. As duas abas dividem o
  // estado: resposta de aba ou campanha que já ficou pra trás é descartada.
  const porEstoque = aba === 'por-estoque';
  useEffect(() => {
    if (!SUGESTAO_POR_LOJA[aba] || !campanhaId) { setSugLojas(null); return undefined; }
    let vivo = true;
    setCarregandoSug(true);
    axios.get(`${API}/plaquinhas/campanha/${campanhaId}/${SUGESTAO_POR_LOJA[aba].rota}`).then(res => {
      if (!vivo) return;
      setSugLojas({ ...res.data, modo: aba });
      const sel = {};
      res.data.lojas.forEach(l => {
        sel[l.filial] = res.data.produtos.filter(p => SUGESTAO_POR_LOJA[aba].sugerido(p, l.filial)).map(p => p.id);
      });
      setSelPorLoja(sel);
      setLojaExpandida(null);
    }).catch(() => { if (vivo) setSugLojas({ modo: aba, lojas: [], produtos: [] }); })
      .finally(() => { if (vivo) setCarregandoSug(false); });
    return () => { vivo = false; };
  }, [aba, campanhaId]);
  // Logo depois de trocar de aba ainda há a resposta da outra: não desenha com ela.
  const sugAtual = sugLojas?.modo === aba ? sugLojas : null;

  const toggleProdLoja = (filial, id) => setSelPorLoja(prev => {
    const cur = prev[filial] || [];
    return { ...prev, [filial]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
  });
  const marcarSugeridos = (filial) => setSelPorLoja(prev => ({
    ...prev, [filial]: sugAtual.produtos.filter(p => SUGESTAO_POR_LOJA[aba].sugerido(p, filial)).map(p => p.id)
  }));
  const limparLoja = (filial) => setSelPorLoja(prev => ({ ...prev, [filial]: [] }));

  const gerarLoja = async (filial) => {
    const ids = selPorLoja[filial] || [];
    if (!ids.length) return Swal.fire('Atenção', 'Nenhum produto selecionado para essa loja', 'warning');
    setGerandoLoja(filial);
    try {
      const res = await axios.post(`${API}/plaquinhas/campanha/${campanhaId}`, { produto_ids: ids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const nome = (filiaisMap[filial] || `filial-${filial}`).replace(/\s+/g, '-');
      const a = document.createElement('a'); a.href = url; a.download = `plaquinhas-${porEstoque ? 'estoque-' : ''}${nome}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Erro', await erroDoPdf(err), 'error');
    } finally { setGerandoLoja(null); }
  };

  useEffect(() => {
    if (campanhaId) {
      axios.get(`${API}/produtos/campanha/${campanhaId}`).then(res => {
        const aprovados = res.data.filter(p => p.aprovado && p.preco_diretoria);
        setProdutosCampanha(aprovados);
        setSelecionados(aprovados.map(p => p.id));
      }).catch(() => {});
    }
  }, [campanhaId]);

  const fmt = (v) => parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Gerar por campanha
  const gerarCampanha = async () => {
    if (!selecionados.length) return Swal.fire('Atenção', 'Selecione ao menos um produto', 'warning');
    setGerando(true);
    try {
      const res = await axios.post(`${API}/plaquinhas/campanha/${campanhaId}`, { produto_ids: selecionados }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `plaquinhas-campanha-${campanhaId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Erro', await erroDoPdf(err), 'error');
    } finally { setGerando(false); }
  };

  // Buscar produtos BDENTER
  const buscarAvulso = async () => {
    if (!buscaCodpro && !buscaDescricao) return;
    const params = new URLSearchParams();
    if (buscaCodpro) params.append('codpro', buscaCodpro);
    if (buscaDescricao) params.append('descricao', buscaDescricao);
    const res = await axios.get(`${API}/produtos/buscar?${params}`);
    setResultados(res.data);
  };

  const adicionarAvulso = (prod) => {
    if (itensAvulsos.find(i => i.codigo === prod.codpro)) return;
    setItensAvulsos(prev => [...prev, {
      codigo: prod.codpro, nome: prod.descricao,
      precoDE: (prod.preco_venda * 1.15).toFixed(2),
      precoPOR: prod.preco_venda.toFixed(2),
      comissao: '', tipo: (prod.disponibilidade || '').toLowerCase().includes('fora') ? 'fora_linha' : 'linha'
    }]);
  };

  const adicionarManual = () => {
    if (!manual.codigo || !manual.precoPOR) return Swal.fire('Atenção', 'Preencha código e preço POR', 'warning');
    setItensAvulsos(prev => [...prev, { ...manual, precoDE: manual.precoDE || (parseFloat(manual.precoPOR) * 1.15).toFixed(2) }]);
    setManual({ codigo: '', nome: '', precoDE: '', precoPOR: '', comissao: '', tipo: 'linha' });
  };

  const editarAvulso = (idx, campo, valor) => {
    setItensAvulsos(prev => prev.map((item, i) => i === idx ? { ...item, [campo]: valor } : item));
  };

  const removerAvulso = (idx) => setItensAvulsos(prev => prev.filter((_, i) => i !== idx));

  // Gerar avulsas
  const gerarAvulsa = async () => {
    if (!itensAvulsos.length) return Swal.fire('Atenção', 'Adicione ao menos um produto', 'warning');
    setGerando(true);
    try {
      const res = await axios.post(`${API}/plaquinhas/avulsa`, { produtos: itensAvulsos }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'plaquinhas-avulsa.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Erro', await erroDoPdf(err), 'error');
    } finally { setGerando(false); }
  };

  return (
    <div>
      <h4 className="fw-bold mb-4"><i className="bi bi-printer me-2"></i>Plaquinhas de Preço</h4>

      {/* Abas */}
      <div className="d-flex gap-2 mb-4">
        <button className={`btn ${aba === 'campanha' ? 'btn-primary-mkt' : 'btn-outline-mkt'}`} onClick={() => setAba('campanha')}>
          <i className="bi bi-megaphone me-1"></i> Por Campanha
        </button>
        <button className={`btn ${aba === 'por-loja' ? 'btn-primary-mkt' : 'btn-outline-mkt'}`} onClick={() => setAba('por-loja')}>
          <i className="bi bi-shop me-1"></i> Por Loja (mostruário)
        </button>
        <button className={`btn ${aba === 'por-estoque' ? 'btn-primary-mkt' : 'btn-outline-mkt'}`} onClick={() => setAba('por-estoque')}>
          <i className="bi bi-box-seam me-1"></i> Por Loja (estoque)
        </button>
        <button className={`btn ${aba === 'avulsa' ? 'btn-primary-mkt' : 'btn-outline-mkt'}`} onClick={() => setAba('avulsa')}>
          <i className="bi bi-plus-square me-1"></i> Avulsa / Manual
        </button>
      </div>

      {/* ABA CAMPANHA */}
      {aba === 'campanha' && (
        <div>
          <div className="card p-3 mb-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Selecione a campanha</label>
                <select className="form-select" value={campanhaId} onChange={e => setCampanhaId(e.target.value)}>
                  <option value="">Selecione...</option>
                  {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome} ({c.qtd_aprovados || 0} aprovados)</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <button className="btn-primary-mkt w-100" onClick={gerarCampanha} disabled={gerando || !selecionados.length}>
                  {gerando ? <><span className="spinner-border spinner-border-sm me-1" />Gerando...</> : <><i className="bi bi-printer me-1"></i>Gerar {selecionados.length} Plaquinhas</>}
                </button>
              </div>
            </div>
          </div>

          <PlaquinhaConfig campanhaId={campanhaId} />

          {produtosCampanha.length > 0 && (() => {
            // Filtrar
            const filtrados = produtosCampanha.filter(p => {
              if (filtroTexto) {
                const txt = filtroTexto.toLowerCase();
                if (!(p.codpro || '').toLowerCase().includes(txt) && !(p.descricao || '').toLowerCase().includes(txt)) return false;
              }
              if (filtroDisp && (p.disponibilidade || '') !== filtroDisp) return false;
              if (filtroClasse && !(p.classe_nome || '').toLowerCase().includes(filtroClasse.toLowerCase())) return false;
              return true;
            });
            // Ordenar
            const ordenados = [...filtrados].sort((a, b) => {
              if (!ordenacao.campo) return 0;
              let va, vb;
              if (ordenacao.campo === 'preco_diretoria') { va = parseFloat(a.preco_diretoria) || 0; vb = parseFloat(b.preco_diretoria) || 0; }
              else if (ordenacao.campo === 'comissao') { va = parseFloat(a.comissao) || 0; vb = parseFloat(b.comissao) || 0; }
              else { va = (a[ordenacao.campo] || '').toString().toLowerCase(); vb = (b[ordenacao.campo] || '').toString().toLowerCase(); }
              if (va < vb) return ordenacao.direcao === 'asc' ? -1 : 1;
              if (va > vb) return ordenacao.direcao === 'asc' ? 1 : -1;
              return 0;
            });
            const toggleSort = (campo) => setOrdenacao(prev => ({ campo, direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc' }));
            const sortIcon = (campo) => ordenacao.campo === campo ? (ordenacao.direcao === 'asc' ? ' ▲' : ' ▼') : '';
            // Disponibilidades e classes únicas
            const disps = [...new Set(produtosCampanha.map(p => p.disponibilidade).filter(Boolean))];
            const selFiltrados = selecionados.filter(id => ordenados.some(p => p.id === id));

            return (
            <div className="card p-3">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h6 className="fw-bold mb-0">Produtos Aprovados ({ordenados.length} de {produtosCampanha.length})</h6>
                <label className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={ordenados.length > 0 && ordenados.every(p => selecionados.includes(p.id))}
                    onChange={e => setSelecionados(prev => e.target.checked ? [...new Set([...prev, ...ordenados.map(p => p.id)])] : prev.filter(id => !ordenados.some(p => p.id === id)))} />
                  Selecionar filtrados ({ordenados.length})
                </label>
              </div>
              {/* Filtros */}
              <div className="d-flex gap-2 mb-2 flex-wrap">
                <input className="form-control form-control-sm" style={{ maxWidth: 250 }} placeholder="Buscar código ou descrição..." value={filtroTexto} onChange={e => setFiltroTexto(e.target.value)} />
                <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={filtroDisp} onChange={e => setFiltroDisp(e.target.value)}>
                  <option value="">Todas disponib.</option>
                  {disps.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <input className="form-control form-control-sm" style={{ maxWidth: 180 }} placeholder="Filtrar classe..." value={filtroClasse} onChange={e => setFiltroClasse(e.target.value)} />
                {(filtroTexto || filtroDisp || filtroClasse) && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => { setFiltroTexto(''); setFiltroDisp(''); setFiltroClasse(''); }}>Limpar</button>
                )}
              </div>
              <div className="tabela-container">
                <table className="table table-mkt table-hover table-sm mb-0">
                  <thead><tr>
                    <th></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('codpro')}>Código{sortIcon('codpro')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('descricao')}>Descrição{sortIcon('descricao')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('classe_nome')}>Classe{sortIcon('classe_nome')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('disponibilidade')}>Disponib.{sortIcon('disponibilidade')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('preco_diretoria')}>Preço Dir.{sortIcon('preco_diretoria')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('comissao')}>Comissão{sortIcon('comissao')}</th>
                    <th>Tipo</th>
                  </tr></thead>
                  <tbody>
                    {ordenados.map(p => (
                      <tr key={p.id}>
                        <td><input type="checkbox" checked={selecionados.includes(p.id)} onChange={() => setSelecionados(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])} /></td>
                        <td>{p.codpro}</td>
                        <td>{p.descricao}</td>
                        <td><small>{p.classe_nome || '—'}</small></td>
                        <td><small>{p.disponibilidade || '—'}</small></td>
                        <td className="fw-bold">{fmt(p.preco_diretoria)}</td>
                        <td>{p.comissao ? `R$ ${fmt(p.comissao)}` : '—'}</td>
                        <td><span className={`badge-${(p.disponibilidade || '').toLowerCase().includes('fora') ? 'preenchimento' : 'ativa'}`}>
                          {(p.disponibilidade || '').toLowerCase().includes('fora') ? 'Fora de Linha' : 'De Linha'}
                        </span></td>
                      </tr>
                    ))}
                    {ordenados.length === 0 && <tr><td colSpan="8" className="text-center text-muted">Nenhum produto encontrado</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* ABAS POR LOJA (mostruário e estoque bruto) */}
      {SUGESTAO_POR_LOJA[aba] && (
        <div>
          <div className="card p-3 mb-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Selecione a campanha</label>
                <select className="form-select" value={campanhaId} onChange={e => setCampanhaId(e.target.value)}>
                  <option value="">Selecione...</option>
                  {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome} ({c.qtd_aprovados || 0} aprovados)</option>)}
                </select>
              </div>
            </div>
            <small className="text-muted d-block mt-2">
              <i className="bi bi-info-circle me-1"></i>
              {porEstoque
                ? <>Cada loja recebe <b>tudo da campanha que tem no estoque dela</b> (estoque bruto, sem descontar reserva nem contar o mostruário), de qualquer classe. Só entram as lojas da campanha. Você pode marcar/desmarcar o que quiser antes de gerar.</>
                : <>Cada loja recebe só os <b>pisos que tem no mostruário</b> (amostra). Produtos que não são piso vão pra todas as lojas. Você pode marcar/desmarcar o que quiser antes de gerar. A Central (04) não tem showroom, fica de fora.</>}
            </small>
          </div>

          <PlaquinhaConfig campanhaId={campanhaId} />

          {carregandoSug && <div className="card p-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2" />Montando sugestão por loja...</div>}

          {!carregandoSug && sugAtual && sugAtual.lojas.length === 0 && (
            <div className="card p-4 text-center text-muted">
              {campanhaId ? 'Nenhum produto aprovado com preço definido nesta campanha (a plaquinha usa os produtos aprovados). Aprove os produtos na tela da campanha.' : 'Selecione uma campanha acima.'}
            </div>
          )}

          {!carregandoSug && sugAtual && sugAtual.lojas.map(l => {
            const sel = selPorLoja[l.filial] || [];
            const expandida = lojaExpandida === l.filial;
            const nPisosAmostra = porEstoque ? 0 : sugAtual.produtos.filter(p => p.isPiso && p.amostra[l.filial] > 0).length;
            const nComEstoque = porEstoque ? sugAtual.produtos.filter(p => p.estoque[l.filial] > 0).length : 0;
            return (
              <div key={l.filial} className="card p-3 mb-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setLojaExpandida(expandida ? null : l.filial)} title="Ver/editar produtos">
                      <i className={`bi bi-chevron-${expandida ? 'up' : 'down'}`}></i>
                    </button>
                    <div>
                      <strong>{filiaisMap[l.filial] || `Filial ${l.filial}`}</strong> <span className="text-muted">({l.filial})</span>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        <b style={{ color: '#6B2C5C' }}>{sel.length}</b> plaquinha(s) · {porEstoque ? `${nComEstoque} produto(s) com estoque` : `${nPisosAmostra} piso(s) no mostruário`}
                      </div>
                    </div>
                  </div>
                  <button className="btn-primary-mkt" onClick={() => gerarLoja(l.filial)} disabled={gerandoLoja === l.filial || !sel.length}>
                    {gerandoLoja === l.filial ? <><span className="spinner-border spinner-border-sm me-1" />Gerando...</> : <><i className="bi bi-printer me-1"></i>Gerar PDF</>}
                  </button>
                </div>

                {expandida && (
                  <>
                    <div className="d-flex gap-2 mt-3 mb-1 flex-wrap">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => marcarSugeridos(l.filial)}><i className="bi bi-magic me-1"></i>Marcar sugeridos</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => limparLoja(l.filial)}>Limpar</button>
                    </div>
                    <div className="tabela-container" style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                      <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                          <tr><th style={{ width: 34 }}></th><th>Código</th><th>Descrição</th><th>{porEstoque ? 'Estoque aqui' : 'Amostra aqui'}</th></tr>
                        </thead>
                        <tbody>
                          {sugAtual.produtos.map(p => {
                            const marcado = sel.includes(p.id);
                            const qtd = (porEstoque ? p.estoque[l.filial] : p.amostra[l.filial]) || 0;
                            const tag = porEstoque
                              ? (qtd > 0
                                ? <span style={{ color: '#1f7a3d', fontWeight: 600 }}>{qtd.toLocaleString('pt-BR')} em estoque</span>
                                : <span style={{ color: '#b23b3b' }}>sem estoque</span>)
                              : !p.isPiso
                                ? <span className="text-muted">não é piso</span>
                                : qtd > 0
                                  ? <span style={{ color: '#1f7a3d', fontWeight: 600 }}>{qtd} no mostruário</span>
                                  : <span style={{ color: '#b23b3b' }}>sem amostra</span>;
                            return (
                              <tr key={p.id} style={{ opacity: marcado ? 1 : 0.5 }}>
                                <td><input type="checkbox" checked={marcado} onChange={() => toggleProdLoja(l.filial, p.id)} /></td>
                                <td className="fw-semibold">{p.codpro}</td>
                                <td>{p.descricao}</td>
                                <td>{tag}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.76rem' }}>Marcado = entra no PDF da loja. Sugerido = {porEstoque ? 'tudo que tem estoque aqui' : 'piso com amostra aqui + não-pisos'}.</small>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ABA AVULSA */}
      {aba === 'avulsa' && (
        <div>
          {/* Busca rápida */}
          <div className="card p-3 mb-3">
            <h6 className="fw-bold mb-3">Buscar Produto no Sistema</h6>
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Código</label>
                <input className="form-control" value={buscaCodpro} onChange={e => setBuscaCodpro(e.target.value)} placeholder="Ex: 06015" />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Descrição</label>
                <input className="form-control" value={buscaDescricao} onChange={e => setBuscaDescricao(e.target.value)} placeholder="Ex: tubo"
                  onKeyDown={e => e.key === 'Enter' && buscarAvulso()} />
              </div>
              <div className="col-md-2"><button className="btn-primary-mkt w-100" onClick={buscarAvulso}>Buscar</button></div>
            </div>
            {resultados.length > 0 && (
              <div className="mt-3" style={{ maxHeight: 250, overflowY: 'auto' }}>
                {resultados.map(r => (
                  <div key={r.codpro} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span style={{ fontSize: '0.85rem' }}><strong>{r.codpro}</strong> — {r.descricao} <small className="text-muted">({r.disponibilidade})</small> — R$ {fmt(r.preco_venda)}</span>
                    <button className="btn btn-sm btn-outline-warning" onClick={() => adicionarAvulso(r)}><i className="bi bi-plus"></i></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulário manual */}
          <div className="card p-3 mb-3">
            <h6 className="fw-bold mb-3">Adicionar Manual</h6>
            <div className="row g-2 align-items-end">
              <div className="col-md-2"><label className="form-label" style={{ fontSize: '0.85rem' }}>Código</label><input className="form-control" value={manual.codigo} onChange={e => setManual(p => ({ ...p, codigo: e.target.value }))} /></div>
              <div className="col-md-3"><label className="form-label" style={{ fontSize: '0.85rem' }}>Nome</label><input className="form-control" value={manual.nome} onChange={e => setManual(p => ({ ...p, nome: e.target.value }))} /></div>
              <div className="col-md-1"><label className="form-label" style={{ fontSize: '0.85rem' }}>DE</label><input type="number" step="0.01" className="form-control" value={manual.precoDE} onChange={e => setManual(p => ({ ...p, precoDE: e.target.value }))} /></div>
              <div className="col-md-1"><label className="form-label" style={{ fontSize: '0.85rem' }}>POR</label><input type="number" step="0.01" className="form-control" value={manual.precoPOR} onChange={e => setManual(p => ({ ...p, precoPOR: e.target.value }))} /></div>
              <div className="col-md-1"><label className="form-label" style={{ fontSize: '0.85rem' }}>Comissão</label><input type="number" step="0.01" className="form-control" value={manual.comissao} onChange={e => setManual(p => ({ ...p, comissao: e.target.value }))} /></div>
              <div className="col-md-2"><label className="form-label" style={{ fontSize: '0.85rem' }}>Tipo</label>
                <select className="form-select" value={manual.tipo} onChange={e => setManual(p => ({ ...p, tipo: e.target.value }))}>
                  <option value="linha">De Linha (verde)</option>
                  <option value="fora_linha">Fora de Linha (azul)</option>
                </select>
              </div>
              <div className="col-md-1"><button className="btn-primary-mkt" onClick={adicionarManual}><i className="bi bi-plus-lg"></i></button></div>
            </div>
          </div>

          {/* Lista de itens avulsos */}
          {itensAvulsos.length > 0 && (
            <div className="card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">Itens para Gerar ({itensAvulsos.length})</h6>
                <button className="btn-primary-mkt" onClick={gerarAvulsa} disabled={gerando}>
                  {gerando ? <><span className="spinner-border spinner-border-sm me-1" />Gerando...</> : <><i className="bi bi-printer me-1"></i>Gerar Plaquinhas</>}
                </button>
              </div>
              <div className="tabela-container">
                <table className="table table-mkt table-hover table-sm mb-0">
                  <thead><tr><th>Código</th><th>Nome</th><th>DE</th><th>POR</th><th>12x (POR/10)</th><th>Comissão</th><th>Tipo</th><th></th></tr></thead>
                  <tbody>
                    {itensAvulsos.map((item, idx) => (
                      <tr key={idx}>
                        <td><input className="form-control form-control-sm" style={{ width: 80 }} value={item.codigo} onChange={e => editarAvulso(idx, 'codigo', e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" value={item.nome} onChange={e => editarAvulso(idx, 'nome', e.target.value)} /></td>
                        <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 80 }} value={item.precoDE} onChange={e => editarAvulso(idx, 'precoDE', e.target.value)} /></td>
                        <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 80 }} value={item.precoPOR} onChange={e => editarAvulso(idx, 'precoPOR', e.target.value)} /></td>
                        <td style={{ color: '#888' }}>{item.precoPOR ? fmt(parseFloat(item.precoPOR) / 10) : '—'}</td>
                        <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 70 }} value={item.comissao} onChange={e => editarAvulso(idx, 'comissao', e.target.value)} /></td>
                        <td>
                          <select className="form-select form-select-sm" style={{ width: 100 }} value={item.tipo} onChange={e => editarAvulso(idx, 'tipo', e.target.value)}>
                            <option value="linha">Linha</option>
                            <option value="fora_linha">Fora</option>
                          </select>
                        </td>
                        <td><button className="btn btn-sm btn-outline-danger" onClick={() => removerAvulso(idx)}><i className="bi bi-x"></i></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {gerando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4 text-center" style={{ minWidth: 320 }}>
            <div className="spinner-border mb-3 mx-auto" style={{ color: '#6B2C5C', width: 48, height: 48 }} />
            <h5 className="fw-bold mb-2">Gerando Plaquinhas</h5>
            <p style={{ fontSize: '0.8rem', color: '#999' }}>Isso pode levar alguns segundos</p>
          </div>
        </div>
      )}
    </div>
  );
}
